// Shared helpers/types for the Catalog Manager product & brand image manager.
//
// Images are linked by CONVENTION (no data writes): a product image is keyed by
// the food id, a brand image by a heavily-sanitized slug of the free-form brand
// name so most spellings of the same brand collapse to one key.

/**
 * Collapse a free-form brand name to a stable lookup slug. Mirrors the Go
 * sanitizeBrandKey in api/products/service/catalog_images.go and MUST stay in
 * sync with it: NFKD-normalize, drop combining marks (accents), lowercase, and
 * keep only [a-z0-9]. e.g. "Trader Joe's®", "trader joes", and "TRADER JOE'S"
 * all become "traderjoes".
 */
export const sanitizeBrandKey = (name: string): string =>
  (name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // combining diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Accept attribute for the file inputs. We accept ANY image the browser will
 * offer (plus a few explicit extensions some OSes report with an empty MIME
 * type). The server sniffs the real bytes and converts non-web formats (e.g.
 * BMP/TIFF) to PNG, so we can afford to be permissive here.
 */
export const IMAGE_ACCEPT =
  "image/*,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tif,.tiff,.avif,.heic,.heif";

/** Client-side size cap; mirrors the server's maxCatalogImageBytes (10 MB). */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * Filenames we treat as images even when the browser reports a blank/oddball
 * MIME type (common for .webp/.heic dragged in from some sources).
 */
const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|tiff?|avif|heic|heif)$/i;

/** Result of an image upload (returned by the products image endpoints). */
export interface CatalogImageResult {
  url: string;
  bucket: string;
  path: string;
  key: string;
  brandName?: string;
}

/** Result of a batch image lookup: only keys that HAVE an image are present. */
export interface CatalogImageLookup {
  /** productId -> public image url */
  products: Record<string, string>;
  /** brandName (as supplied) -> public image url */
  brands: Record<string, string>;
}

/**
 * Validate a chosen/dropped file before upload. Returns an error string, or ""
 * when the file is an acceptable image within the size cap.
 */
export const validateImageFile = (file: File): string => {
  // Accept by MIME OR by extension: some sources hand us an image file with an
  // empty/incorrect `type`, and the server validates the real bytes anyway.
  const looksImage =
    file.type.startsWith("image/") || IMAGE_EXT_RE.test(file.name);
  if (!looksImage) return "Please choose an image file";
  if (file.size > MAX_IMAGE_BYTES) return "Image is too large (max 10 MB)";
  return "";
};
